import { useMemo, useState } from "react";
import { useController } from "react-hook-form";
import { useGetList, useTranslate } from "ra-core";
import { X } from "lucide-react";
import { Input } from "@/components/ds/ui/input";
import { Button } from "@/components/ds/ui/button";
import {
  FormLabel,
  FormField,
  FormControl,
  FormError,
} from "@/components/admin/form";
import { FieldTitle } from "ra-core";

interface MultiAssigneeInputProps {
  source: string;
  label?: string;
  helperText?: false | string;
}

/**
 * Multi-select for sales reps. Stores an array of sales_id values.
 * Renders selected reps as chips; search filters the dropdown list.
 */
export const MultiAssigneeInput = ({
  source,
  label,
  helperText,
}: MultiAssigneeInputProps) => {
  const translate = useTranslate();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const {
    field: { value = [], onChange },
    fieldState: { error },
  } = useController({ name: source });

  const selectedIds: number[] = Array.isArray(value) ? value : [];

  // Fetch all sales reps once (small table)
  const { data: salesRaw } = useGetList("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const sales = Array.isArray(salesRaw) ? salesRaw : (salesRaw?.data ?? []);

  const selectedSales = useMemo(
    () => sales.filter((s: any) => selectedIds.includes(Number(s.id))),
    [sales, selectedIds],
  );

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sales
      .filter((s: any) => !selectedIds.includes(Number(s.id)))
      .filter((s: any) =>
        q
          ? `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
            (s.email ?? "").toLowerCase().includes(q)
          : true,
      )
      .slice(0, 8);
  }, [sales, selectedIds, search]);

  const addAssignee = (id: number) => {
    onChange([...selectedIds, id]);
    setSearch("");
    setOpen(false);
  };

  const removeAssignee = (id: number) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  return (
    <FormField id={source} name={source}>
      {label !== undefined && (
        <FormLabel>
          <FieldTitle
            label={label ?? translate("crm.task.field.assigned_to")}
            source={source}
          />
        </FormLabel>
      )}
      <FormControl>
        <div className="space-y-2">
          {/* Selected chips */}
          {selectedSales.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedSales.map((s: any) => (
                <span
                  key={s.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs"
                >
                  {s.first_name} {s.last_name}
                  <button
                    type="button"
                    onClick={() => removeAssignee(Number(s.id))}
                    className="hover:text-destructive"
                    aria-label={`Remove ${s.first_name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder={translate("crm.task.assignee.search", {
                _: "Search sales reps...",
              })}
            />
            {open && available.length > 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-auto">
                {available.map((s: any) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => addAssignee(Number(s.id))}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                  >
                    {s.first_name} {s.last_name}
                    {s.email && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {s.email}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </FormControl>
      {helperText !== false && helperText && (
        <p className="text-xs text-muted-foreground mt-1">{helperText}</p>
      )}
      <FormError>{error?.message}</FormError>
    </FormField>
  );
};
